// useScrollAnimation.js - Custom React hook for scroll animations
// A "hook" is a reusable function that adds behavior to React components
// This hook uses the Intersection Observer API to detect when elements
// enter the viewport and adds a 'visible' class to trigger CSS animations

import { useEffect } from 'react';

function useScrollAnimation() {
  useEffect(() => {
    // IntersectionObserver watches elements and fires a callback
    // when they enter or leave the viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Element has entered the viewport — add visible class
            // This triggers the CSS transition defined in App.css
            entry.target.classList.add('visible');

            // Once visible we stop watching the element
            // so the animation only plays once
            observer.unobserve(entry.target);
          }
        });
      },
      {
        // threshold: 0.1 means the callback fires when
        // 10% of the element is visible in the viewport
        threshold: 0.1,

        // rootMargin adds a 50px buffer below the viewport
        // so elements start animating slightly before they're fully visible
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Find all elements with the fade-in-up class and watch them
    const elements = document.querySelectorAll('.fade-in-up');
    elements.forEach(el => observer.observe(el));

    // Cleanup — stop observing when component unmounts
    return () => observer.disconnect();
  }, []);
}

export default useScrollAnimation;